import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  ORG_CHART_IMPORT_FORMATS,
  ORG_CHART_IMPORT_MAX_BYTES,
  OrgChartImportStateService,
} from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-import-state.service';

/**
 * Modal "Importar organograma" do builder do Grupo Econômico (card 3578): escolhe o arquivo (PDF, PNG ou JPG até
 * 20 MB) e envia. A leitura segue em segundo plano; quem mostra o andamento é o `app-org-chart-import-progress-card`.
 * Usa o `OrgChartImportStateService` provido pelo builder.
 */
@Component({
  selector: 'app-org-chart-import-upload-modal',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './org-chart-import-upload-modal.component.html',
  styleUrls: ['./org-chart-import-upload-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgChartImportUploadModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  readonly state = inject(OrgChartImportStateService);
  private readonly toast = inject(ToastHelperService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly acceptedFormats = ORG_CHART_IMPORT_FORMATS;
  readonly selectedFile = signal<File | null>(null);
  /** Já havia importação aberta ao abrir a modal: enviar outra substitui a atual. */
  readonly replacesOpenImport = signal(false);

  readonly isFormValid = computed(() => !!this.selectedFile() && !this.state.isStarting());
  readonly selectedFileSize = computed(() => formatFileSize(this.selectedFile()?.size ?? 0));

  constructor() {
    super(inject(TranslateService));
  }

  open(): void {
    this.selectedFile.set(null);
    this.replacesOpenImport.set(this.state.isOpen());
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'org-chart-import-upload-modal', false, false);
  }

  close(): void {
    this.dialogRef?.close();
  }

  onFileSelected(files: File[]): void {
    const file = files?.[0];
    if (!file) return;

    if (file.size > ORG_CHART_IMPORT_MAX_BYTES) {
      this.toast.showToastError(
        'Arquivo maior que 20 MB',
        'Reduza o arquivo (exporte o PDF em qualidade menor ou envie só a página do organograma) e escolha de novo.'
      );
      return;
    }

    this.selectedFile.set(file);
  }

  clearFile(): void {
    this.selectedFile.set(null);
  }

  submitAction(): void {
    const file = this.selectedFile();
    if (!file || !this.isFormValid()) return;

    this.state
      .start(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.close(),
        error: error => this.formErrorHandler.showToastErrors(error),
      });
  }

  onDialogClosed(): void {
    this.selectedFile.set(null);
  }
}

/** Tamanho legível para a linha do arquivo escolhido ("1,4 MB", "820 KB"). */
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
